import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { AreaLayout } from "@/pages/area/AreaLayout";
import { AppIcon } from "@/components/AppIcon";
import { ArrowRight, Lock } from "lucide-react";

export default function AreaDashboard() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/area/areas").then(({ data }) => setAreas(data)).catch(() => setAreas([])).finally(() => setLoading(false));
  }, []);

  return (
    <AreaLayout subtitle="Le tue aree" title="Aree riservate">
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-xl bg-white border border-slate-200 animate-pulse" />)}
        </div>
      ) : areas.length === 0 ? (
        <div data-testid="area-empty" className="flex flex-col items-center justify-center text-center py-24 rounded-2xl border border-dashed border-slate-300 bg-white">
          <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4"><Lock className="h-6 w-6 text-slate-400" /></div>
          <h3 className="font-heading text-xl text-slate-900 mb-1">Nessuna area disponibile</h3>
          <p className="text-slate-500 max-w-sm">Non ti è stata assegnata alcuna area riservata. Contatta l'amministratore.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {areas.map((a, i) => (
            <motion.button
              key={a.area_id}
              data-testid={`area-tile-${a.area_id}`}
              onClick={() => navigate(`/area-riservata/${a.area_id}`)}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.06 }}
              className="group text-left rounded-xl bg-white border border-slate-200 p-6 shadow-sm hover:border-slate-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
            >
              <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${a.color}1A`, color: a.color }}>
                <AppIcon name={a.icon} className="h-6 w-6" />
              </div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{a.client_name}</p>
              <h3 className="font-heading text-lg font-medium text-slate-900">{a.title}</h3>
              {(a.internal_route === "/easytrasporto" || (a.client_name || "").toLowerCase().includes("anffas")) && (
                <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 mt-1.5" data-testid="area-benefit-badge">
                  Benefit ETS — ANFFAS
                </span>
              )}
              <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem] mt-1">{a.description}</p>
              <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-brand-blue transition-colors">
                Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </AreaLayout>
  );
}
